<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="760" scrollable>
    <v-card class="rounded-xl overflow-hidden">
      <v-toolbar flat color="primary" dark dense>
        <v-icon left small>mdi-clipboard-check-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-bold">Calidad de los datos</v-toolbar-title>
        <v-spacer />
        <v-btn icon small aria-label="Actualizar" title="Actualizar" :loading="cargando" @click="cargar">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon small aria-label="Cerrar calidad de los datos" @click="$emit('update:dialog', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4 cuerpo-calidad">
        <div v-if="cargando && !cargado" class="d-flex justify-center pa-10">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="!armarios.length" class="text-center pa-10 grey--text">
          <v-icon size="48" color="grey lighten-2">mdi-family-tree</v-icon>
          <p class="mt-2 mb-0">Este cliente todavía no tiene armarios.</p>
        </div>

        <template v-else>
          <!-- Resumen -->
          <div class="resumen d-flex align-center flex-wrap">
            <v-progress-circular :value="puntuacion" :color="colorPuntuacion" size="92" width="9" rotate="-90" class="mr-5">
              <span class="puntuacion">{{ puntuacion }}%</span>
            </v-progress-circular>
            <div class="flex-grow-1">
              <div class="text-h6 font-weight-bold">{{ textoPuntuacion }}</div>
              <div class="text-body-2 grey--text text--darken-1">
                {{ evaluados.length }} armario{{ evaluados.length === 1 ? "" : "s" }} revisado{{ evaluados.length === 1 ? "" : "s" }}
                <template v-if="soloFinales && armarios.length !== evaluados.length">
                  (los que no tienen otros dentro; {{ armarios.length }} en total)
                </template>
              </div>
              <v-switch
                v-model="soloFinales"
                dense
                hide-details
                inset
                class="mt-2"
                label="Revisar solo los armarios finales (sin otros dentro)"
              />
            </div>
          </div>

          <!-- Indicadores -->
          <v-expansion-panels v-model="abierto" accordion class="mt-4 indicadores">
            <v-expansion-panel v-for="ind in indicadores" :key="ind.clave" :disabled="!ind.lista.length && !ind.aviso">
              <v-expansion-panel-header>
                <div class="d-flex align-center indicador-cabecera">
                  <v-icon :color="ind.aviso ? 'grey' : ind.lista.length ? ind.color : 'success'" class="mr-3">
                    {{ ind.aviso ? "mdi-information-outline" : ind.lista.length ? ind.icono : "mdi-check-circle" }}
                  </v-icon>
                  <div class="flex-grow-1 mr-4">
                    <div class="font-weight-bold">{{ ind.titulo }}</div>
                    <div class="text-caption grey--text text--darken-1">
                      <template v-if="ind.aviso">{{ ind.aviso }}</template>
                      <template v-else-if="!ind.lista.length">Todo correcto</template>
                      <template v-else>{{ ind.lista.length }} de {{ ind.total }} · {{ ind.ayuda }}</template>
                    </div>
                    <v-progress-linear
                      v-if="!ind.aviso"
                      :value="ind.total ? 100 - (ind.lista.length / ind.total) * 100 : 100"
                      :color="ind.lista.length ? ind.color : 'success'"
                      background-color="grey lighten-3"
                      height="5"
                      rounded
                      class="mt-1"
                    />
                  </div>
                  <span v-if="!ind.aviso" class="porcentaje" :class="ind.lista.length ? '' : 'success--text'">
                    {{ ind.total ? Math.round(100 - (ind.lista.length / ind.total) * 100) : 100 }}%
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="lista-pendientes">
                  <li v-for="item in ind.lista" :key="item.clave" class="d-flex align-center">
                    <div class="flex-grow-1 min-w-0">
                      <a href="#" class="nombre-pendiente" :title="ind.tipo === 'plano' ? 'Abrir el plano' : 'Ver en el árbol'" @click.prevent="ind.tipo === 'plano' ? $emit('abrir-plano', item.plano) : $emit('ver-armario', item.id)">
                        {{ item.nombre }}
                      </a>
                      <div v-if="item.ruta || item.nota" class="text-caption grey--text">
                        {{ item.ruta }}<template v-if="item.ruta && item.nota"> · </template>{{ item.nota }}
                      </div>
                    </div>
                    <v-btn
                      v-for="accion in ind.acciones"
                      :key="accion.evento"
                      icon
                      small
                      :title="accion.titulo"
                      :aria-label="`${accion.titulo}: ${item.nombre}`"
                      @click="$emit(accion.evento, item.nodo)"
                    >
                      <v-icon small>{{ accion.icono }}</v-icon>
                    </v-btn>
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const API = `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs`;
const ANIOS_DOCUMENTO_ANTIGUO = 2;

// Qué le falta a un cliente para tener sus armarios bien documentados
export default {
  name: "DialogCalidadDatos",
  props: {
    dialog: { type: Boolean, default: false },
    idCliente: { type: [Number, String], default: null },
    arbol: { type: Array, default: () => [] },
    planos: { type: Array, default: () => [] },
    puedeEditar: { type: Boolean, default: false },
    verPlanos: { type: Boolean, default: true },
  },
  data() {
    return {
      cargando: false,
      cargado: false,
      externo: false,
      documentos: {}, // { id_armario: { documentos, ultimo } } (clientes locales)
      carpetasPorArmario: {}, // { id_armario: [{ nombre, documentos }] } carpetas activas asignadas
      armariosEnPlano: {}, // { id_armario: [nombres de plano] }
      armariosPorPlano: {}, // { id_plano: nº de armarios }
      soloFinales: true,
      abierto: null,
    };
  },
  computed: {
    ...mapState("armarios", ["categoriaPorArmario"]),
    // armarios en orden de árbol, con la ruta de sus padres
    armarios() {
      const lista = [];
      const recorrer = (nodos, ruta, ancestros) => {
        for (const n of nodos || []) {
          lista.push({ id: n.id, nombre: n.name, ruta: ruta.join(" › "), ancestros, final: !(n.children && n.children.length), nodo: n });
          recorrer(n.children, [...ruta, n.name], [...ancestros, n.id]);
        }
      };
      recorrer(this.arbol, [], []);
      return lista;
    },
    evaluados() {
      return this.soloFinales ? this.armarios.filter((a) => a.final) : this.armarios;
    },
    indicadores() {
      const total = this.evaluados.length;
      const numDocumentos = (a) => (this.externo ? Number(a.nodo.adjuntos_count) || 0 : this.documentos[a.id]?.documentos || 0);
      const accionesDocs = [{ evento: "ver-documentos", icono: "mdi-file-document-multiple-outline", titulo: "Ver documentación" }];
      const lista = [];

      lista.push({
        clave: "documentos",
        titulo: "Armarios sin documentación",
        ayuda: "no tienen ningún documento adjunto",
        icono: "mdi-file-alert-outline",
        color: "error",
        total,
        lista: this.evaluados.filter((a) => numDocumentos(a) === 0),
        acciones: accionesDocs,
      });

      if (this.verPlanos) {
        const hayPlanos = this.planos.length > 0;
        lista.push({
          clave: "planos",
          titulo: "Armarios que no están en ningún plano",
          ayuda: "ni ellos ni el armario que los contiene están en un plano",
          icono: "mdi-map-marker-off-outline",
          color: "orange darken-2",
          total,
          aviso: hayPlanos ? null : "El cliente todavía no tiene planos",
          // se puede localizar si está en un plano él o alguno de los armarios que lo contienen
          lista: hayPlanos ? this.evaluados.filter((a) => ![a.id, ...a.ancestros].some((id) => this.armariosEnPlano[id])) : [],
          acciones: [],
        });
      }

      lista.push({
        clave: "categorias",
        titulo: "Armarios sin categoría",
        ayuda: "no se pueden filtrar ni marcar con su símbolo",
        icono: "mdi-palette-outline",
        color: "deep-purple",
        total,
        lista: this.evaluados.filter((a) => !this.categoriaPorArmario[a.id]),
        acciones: this.puedeEditar ? [{ evento: "asignar-categoria", icono: "mdi-palette-outline", titulo: "Asignar categoría" }] : [],
      });

      const limite = new Date();
      limite.setFullYear(limite.getFullYear() - ANIOS_DOCUMENTO_ANTIGUO);
      const conDocumentos = this.evaluados.filter((a) => numDocumentos(a) > 0);
      lista.push({
        clave: "antiguos",
        titulo: `Documentación sin actualizar en más de ${ANIOS_DOCUMENTO_ANTIGUO} años`,
        ayuda: "su documento más reciente es antiguo",
        icono: "mdi-calendar-alert",
        color: "amber darken-3",
        total: conDocumentos.length,
        aviso: this.externo
          ? "No disponible para clientes con portal externo"
          : !conDocumentos.length ? "Ningún armario tiene documentos todavía" : null,
        lista: this.externo
          ? []
          : conDocumentos
              .filter((a) => this.documentos[a.id]?.ultimo && new Date(this.documentos[a.id].ultimo) < limite)
              .map((a) => ({ ...a, nota: `último: ${new Date(this.documentos[a.id].ultimo).toLocaleDateString("es-ES")}` })),
        acciones: accionesDocs,
      });

      const conCarpetas = this.evaluados.filter((a) => this.carpetasPorArmario[a.id]);
      lista.push({
        clave: "carpetas-vacias",
        titulo: "Armarios con carpetas vacías",
        ayuda: "tienen carpetas asignadas sin ningún documento",
        icono: "mdi-folder-alert-outline",
        color: "amber darken-2",
        total: conCarpetas.length,
        aviso: conCarpetas.length ? null : "Ningún armario tiene carpetas asignadas",
        lista: conCarpetas
          .map((a) => ({ ...a, vacias: this.carpetasPorArmario[a.id].filter((c) => !c.documentos) }))
          .filter((a) => a.vacias.length)
          .map(({ vacias, ...a }) => ({ ...a, nota: `falta: ${vacias.map((c) => c.nombre).join(", ")}` })),
        acciones: accionesDocs,
      });

      if (this.verPlanos && this.planos.length) {
        const vacios = this.planos.filter((p) => !this.armariosPorPlano[p.id]);
        lista.push({
          clave: "planos-vacios",
          tipo: "plano",
          titulo: "Planos sin ningún armario",
          ayuda: "se han subido pero no tienen nada colocado",
          icono: "mdi-floor-plan",
          color: "blue-grey",
          total: this.planos.length,
          lista: vacios.map((p) => ({ clave: `plano-${p.id}`, nombre: p.nombre, plano: p, nota: p.descripcion || "" })),
          acciones: [],
        });
      }

      return lista.map((ind) => ({ ...ind, lista: ind.lista.map((x) => ({ clave: x.clave || x.id, ...x })) }));
    },
    // media de lo que está bien en cada indicador aplicable
    puntuacion() {
      const validos = this.indicadores.filter((i) => !i.aviso && i.total > 0);
      if (!validos.length) return 100;
      const media = validos.reduce((s, i) => s + (1 - i.lista.length / i.total), 0) / validos.length;
      return Math.round(media * 100);
    },
    colorPuntuacion() {
      if (this.puntuacion >= 85) return "success";
      if (this.puntuacion >= 60) return "amber darken-2";
      return "error";
    },
    textoPuntuacion() {
      if (this.puntuacion >= 85) return "Bien documentado";
      if (this.puntuacion >= 60) return "Documentación a medias";
      return "Falta bastante documentación";
    },
  },
  watch: {
    dialog(abierto) {
      if (abierto) this.cargar();
    },
    // al volver de subir documentos o colocar armarios, el árbol se recarga: recalcular
    arbol() {
      if (this.dialog) this.cargar();
    },
  },
  methods: {
    async cargar() {
      if (!this.idCliente) return;
      this.cargando = true;
      try {
        const [docs, ...porPlano] = await Promise.all([
          axios.get(`${API}/clientes/${this.idCliente}/documentos-por-armario`),
          ...(this.verPlanos ? this.planos : []).map((p) =>
            axios
              .get(`${API}/armarios/plano/${p.id}`, { params: { id_cliente: this.idCliente } })
              .then((r) => ({ plano: p, armarios: Array.isArray(r.data) ? r.data : [] }))
              .catch(() => ({ plano: p, armarios: [] }))
          ),
        ]);
        this.externo = !!docs.data.externo;
        this.documentos = Object.fromEntries((docs.data.armarios || []).map((d) => [d.id_armario, d]));
        const carpetas = {};
        for (const c of docs.data.carpetas || []) (carpetas[c.id_armario] = carpetas[c.id_armario] || []).push(c);
        this.carpetasPorArmario = carpetas;

        const enPlano = {};
        const cuenta = {};
        for (const { plano, armarios } of porPlano) {
          cuenta[plano.id] = armarios.length;
          for (const a of armarios) {
            const id = a.id_armario ?? a.id;
            (enPlano[id] = enPlano[id] || []).push(plano.nombre);
          }
        }
        this.armariosEnPlano = enPlano;
        this.armariosPorPlano = cuenta;
        this.cargado = true;
      } catch (error) {
        console.error("Error al calcular la calidad de los datos:", error);
        this.$avisar("No se pudo calcular la calidad de los datos", "error");
      } finally {
        this.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
.cuerpo-calidad {
  max-height: 78vh;
}
.resumen {
  padding: 12px 16px;
  border-radius: 12px;
  background: #f5f8fa;
}
.puntuacion {
  font-size: 1.35rem;
  font-weight: 800;
  color: #37474f;
}
.indicador-cabecera {
  width: 100%;
}
.porcentaje {
  min-width: 44px;
  text-align: right;
  font-weight: 700;
  color: #546e7a;
}
.lista-pendientes {
  list-style: none;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
}
.lista-pendientes li {
  padding: 4px 0;
  border-bottom: 1px solid #f0f2f4;
}
.nombre-pendiente {
  font-weight: 600;
  color: #117f9c;
  text-decoration: none;
}
.nombre-pendiente:hover {
  text-decoration: underline;
}
.min-w-0 {
  min-width: 0;
}
</style>
